/**
 * EditAddressModal
 *
 * Modal shell around `AddressForm`. Owns dialog chrome, submit lifecycle
 * (`loading` flag, submit-error capture) and the open/close plumbing. The form
 * itself handles field rendering; form state is owned by `useAddressFormState`.
 *
 * Uses `useTranslation('modules.modals.address')` for chrome labels.
 */
import * as React from 'react';
import { useState } from 'react';
import type { Address } from '../../graphql/generated/graphql';
import type { ValidationIssue } from '../../types';
import { FormModal } from '../../components/FormModal';
import { useTranslation } from '../../providers/UIProvider';
import { AddressForm } from '../forms/address/AddressForm';
import { useAddressFormState } from '../forms/address/hooks';
import { toAddress } from '../forms/address/utils';

// Re-export types so existing imports from `EditAddressModal` keep working.
export { AddressForm, type AddressFormState } from '../forms/address/AddressForm';

export interface EditAddressModalProps {
  open: boolean;
  onClose: () => void;
  value: Address | null | undefined;
  onSave: (address: Address | null) => void | Promise<void>;
  /** Optional modal title override. Defaults to the i18n key `title`. */
  title?: string;
  /** External validation issues (e.g. invoice-level cross-field checks). */
  errors?: ValidationIssue[];
}

export const EditAddressModal: React.FC<EditAddressModalProps> = ({
  open,
  onClose,
  value,
  onSave,
  title,
  errors
}) => {
  const t = useTranslation('modules.modals.address');
  const state = useAddressFormState({ open, value });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    state.setSubmitError(null);
    try {
      await onSave(toAddress(state.form));
      onClose();
    } catch (err) {
      state.setSubmitError(err instanceof Error ? err.message : t('errors.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      open={open}
      title={title ?? t('title')}
      description={t('description')}
      submitLabel={t('actions.save')}
      cancelLabel={t('actions.cancel')}
      loading={loading}
      error={state.submitError}
      onSubmit={handleSubmit}
      onClose={onClose}
      size='sm'
    >
      <AddressForm
        value={state.form}
        onFieldChange={state.setField}
        disabled={loading}
        errors={errors}
      />
    </FormModal>
  );
};

EditAddressModal.displayName = 'EditAddressModal';
